import React, { useState, useMemo, useRef } from "react";
import { Search, Loader2 } from "lucide-react";
import { ScheduleItem, VisualGroup } from "../types";
import { TYPE_COLORS } from "../constants";
import { toLocalMidnight, formatDate, getDaysDiff, getDayConfig } from "../utils";
import ExportMenu from "./ExportMenu";
import ScheduleModal from "./ScheduleModal";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";

interface GanttViewProps {
  data: ScheduleItem[];
}

const GanttView: React.FC<GanttViewProps> = ({ data }) => {
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("ALL");
  const [isExportingImage, setIsExportingImage] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  
  const { minDate, maxDate, dateRange } = useMemo(() => {
    if (data.length === 0) return { minDate: new Date(), maxDate: new Date(), dateRange: [] };
    let min = toLocalMidnight(new Date(data[0].startDate));
    let max = toLocalMidnight(new Date(data[0].endDate));
    data.forEach(d => {
      const s = toLocalMidnight(new Date(d.startDate));
      const e = toLocalMidnight(new Date(d.endDate));
      if (s < min) min = s;
      if (e > max) max = e;
    });
    min.setDate(min.getDate() - 2);
    max.setDate(max.getDate() + 5);

    const dates = [];
    let curr = new Date(min);
    while (curr <= max) {
      dates.push(new Date(curr));
      curr.setDate(curr.getDate() + 1);
    }
    return { minDate: min, maxDate: max, dateRange: dates };
  }, [data]);

  const today = toLocalMidnight(new Date());

  const groupedData: VisualGroup[] = useMemo(() => {
    let filtered = data.filter(item => {
      if (filterType !== 'ALL' && item.type !== filterType) return false;
      if (searchTerm && !item.teacher.includes(searchTerm) && !item.groupCode.includes(searchTerm)) return false;
      return true;
    });
    
    const groups: { [key: string]: ScheduleItem[] } = {};
    filtered.forEach(item => {
      if (!groups[item.teacher]) groups[item.teacher] = [];
      groups[item.teacher].push(item);
    });

    return Object.entries(groups)
      .map(([name, items]) => {
         const sortedItems = items.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
         const lanes: ScheduleItem[][] = [];
         sortedItems.forEach(item => {
            let placed = false;
            for (let i = 0; i < lanes.length; i++) {
               const lastItemInLane = lanes[i][lanes[i].length - 1];
               if (item.startDate.getTime() > lastItemInLane.endDate.getTime()) {
                  lanes[i].push(item);
                  placed = true;
                  break;
               }
            }
            if (!placed) lanes.push([item]);
         });
         return { name, items: sortedItems, lanes };
      })
      .sort((a, b) => {
         const aActive = a.items.some(i => i.endDate >= today);
         const bActive = b.items.some(i => i.endDate >= today);
         if (aActive && !bActive) return -1;
         if (!aActive && bActive) return 1;
         return 0; 
      });
  }, [data, filterType, searchTerm, today]);

  // 从原始行数据中提取上传文件里可能存在的群号字段（仅读取，不做猜测映射）
  const getRawGroupFromItem = (item: ScheduleItem) => {
    if (!item.raw || typeof item.raw !== 'object') return null;
    const preferKeys = ['群号', '群名称', '群名', '班级', '群ID', 'group', 'groupCode'];
    const rawKeys = Object.keys(item.raw);
    for (const k of preferKeys) {
      const found = rawKeys.find(rk => rk.replace(/\s/g, '').toLowerCase() === k.replace(/\s/g, '').toLowerCase());
      if (found) {
        const val = item.raw[found];
        if (val !== undefined && val !== null && String(val).trim() !== '') return String(val).trim();
      }
    }
    return null;
  };

  const handleExportExcel = () => {
    const exportData = groupedData.flatMap(group => 
       group.items.map(item => ({
          "老师": item.teacher,
          "群号": item.groupCode,
          "类型": TYPE_COLORS[item.type]?.label || item.type,
          "开始日期": formatDate(item.startDate),
          "结束日期": formatDate(item.endDate),
          "学员数": item.studentCount,
          "原始表名": item.sourceSheet,
          "是否预测": item.isPredicted ? "是" : "否"
       }))
    );
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "排班明细");
    XLSX.writeFile(wb, `排班明细_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleExportImage = async () => {
    if (!chartRef.current) return;
    setIsExportingImage(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      const canvas = await html2canvas(chartRef.current, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: chartRef.current.scrollWidth, windowHeight: chartRef.current.scrollHeight });
      const link = document.createElement('a');
      link.download = `排班甘特图_${new Date().toISOString().slice(0,10)}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) { console.error(error); alert("图片导出失败"); } finally { setIsExportingImage(false); }
  };
  
  const handleExportHTML = () => {
    const reportDate = new Date().toLocaleDateString('zh-CN');
    const reportData = {
      generatedAt: reportDate,
      todayTime: today.getTime(),
      colors: TYPE_COLORS,
      teachers: groupedData.map(g => ({
         name: g.name,
         itemsCount: g.items.length,
         flattenedItems: g.lanes.flatMap((lane, laneIndex) => 
            lane.map(item => ({ ...item, startDate: item.startDate.toISOString(), endDate: item.endDate.toISOString(), laneIndex }))
         ),
         totalLanes: g.lanes.length
      })),
      dateRange: dateRange.map(d => ({ val: d.toISOString(), display: formatDate(d), day: ['日','一','二','三','四','五','六'][d.getDay()], isToday: d.getTime() === today.getTime() }))
    };
    alert("请使用预排决策界面的导出功能以获得增强版报表");
  };

  return (
    <div className="backdrop-blur-sm bg-white/90 rounded-2xl shadow-2xl border border-slate-200/60 flex flex-col h-[calc(100vh-140px)] overflow-hidden">
      <div className="p-5 border-b border-slate-200/60 flex flex-wrap gap-4 items-center justify-between bg-gradient-to-r from-slate-50 to-blue-50/40 rounded-t-2xl">
         <div className="flex items-center gap-4">
           <div className="relative group">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-blue-500 transition-colors" size={16} />
             <input 
               type="text" 
               placeholder="搜索老师或群号..." 
               value={searchTerm} 
               onChange={e => setSearchTerm(e.target.value)} 
               className="pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-72 bg-white/80 backdrop-blur-sm transition-all hover:bg-white hover:shadow-md" 
             />
           </div>
           <div className="flex bg-white/80 backdrop-blur-sm rounded-xl border border-slate-300 p-1 shadow-sm">
             {['ALL', '21Day', '15Day', 'Referral', 'Xianyu'].map(t => (
               <button 
                 key={t} 
                 onClick={() => setFilterType(t)} 
                 className={`px-4 py-2 text-xs font-bold rounded-lg transition-all duration-300 ${filterType === t ? 'bg-gradient-to-r from-slate-800 to-slate-700 text-white shadow-lg scale-105' : 'text-slate-600 hover:bg-slate-100 hover:scale-105'}`}
               >
                 {t === 'ALL' ? '全部' : TYPE_COLORS[t as keyof typeof TYPE_COLORS]?.label || t}
               </button>
             ))}
           </div>
         </div>
         <div className="flex items-center gap-4">
             {isExportingImage ? <div className="flex items-center gap-2 text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg text-sm font-medium"><Loader2 size={16} className="animate-spin" /> 生成中...</div> : <ExportMenu onExportExcel={handleExportExcel} onExportImage={handleExportImage} onExportHTML={handleExportHTML} />}
             <div className="flex items-center gap-4 border-l border-gray-200 pl-4">
                <div className="text-xs text-slate-400 flex items-center gap-4">
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-white border border-slate-300"></div><span>确定排期</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-indigo-50 border-dashed border-slate-300 relative overflow-hidden"><div className="absolute inset-0 opacity-20 bg-[repeating-linear-gradient(45deg,#000_0px,#000_2px,transparent_2px,transparent_4px)]"></div></div><span>智能排期</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 border-l-2 border-red-500 h-full"></div><span>今天</span></div>
                </div>
             </div>

             {/* Legend: types + predicted vs uploaded */}
             <div className="ml-4 flex items-center gap-3">
               {Object.keys(TYPE_COLORS).map((k) => {
                 const key = k as keyof typeof TYPE_COLORS;
                 const cfg = TYPE_COLORS[key];
                 return (
                   <div key={key} className="flex items-center gap-2 text-xs text-slate-600">
                     <div className={`w-3 h-3 rounded-sm ${cfg.bg} border ${cfg.border}`} />
                     <div className="whitespace-nowrap">{cfg.label}</div>
                   </div>
                 );
               })}

               <div className="flex items-center gap-2 text-xs text-slate-600">
                 <div className="w-6 h-3 rounded-sm bg-slate-200 border border-slate-300" />
                 <div>已上传排班</div>
               </div>
               <div className="flex items-center gap-2 text-xs text-slate-600">
                 <div className="w-6 h-3 rounded-sm bg-slate-200 border border-slate-300 relative overflow-hidden">
                   <div className="absolute inset-0 opacity-30 bg-[repeating-linear-gradient(45deg,rgba(0,0,0,0.06) 0px, rgba(0,0,0,0.06) 4px, transparent 4px, transparent 8px)]"></div>
                 </div>
                 <div>预测排班</div>
               </div>
             </div>
         </div>
      </div>

      <div className="flex-1 overflow-auto relative bg-white" ref={chartRef}>
         <div className="inline-block min-w-full">
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200 flex shadow-sm">
               <div className="sticky left-0 z-30 w-40 bg-gray-50 border-r border-gray-200 p-3 font-bold text-slate-600 text-sm flex items-center justify-center shadow-[2px_0_5px_rgba(0,0,0,0.05)]">
                 老师 ({groupedData.length})
               </div>
               <div className="flex">
                 {dateRange.map((d, i) => {
                   const isToday = d.getTime() === today.getTime();
                   const weekStr = ['日','一','二','三','四','五','六'][d.getDay()];
                   const dateStr = formatDate(d);
                   return (
                     <div key={i} style={{ width: 40, minWidth: 40 }} className={`flex-shrink-0 text-center border-r border-gray-100 py-1.5 relative ${isToday ? 'bg-red-50' : 'bg-white'}`}>
                        {isToday && <div className="absolute inset-y-0 left-0 border-l-2 border-red-400 z-10"></div>}
                        <div className={`text-xs font-bold mb-0.5 ${isToday ? 'text-red-500' : 'text-slate-500'}`}>{weekStr}</div>
                        <div className={`text-[10px] font-mono leading-none ${isToday ? 'text-red-600 font-bold' : 'text-slate-400'}`}>{dateStr}</div>
                     </div>
                   );
                 })}
               </div>
            </div>

            <div className="divide-y divide-gray-100 bg-white">
               {groupedData.map((group, gIdx) => {
                 const rowHeight = Math.max(80, group.lanes.length * 42 + 20);
                 return (
                   <div key={gIdx} className="flex hover:bg-slate-50 transition-colors group" style={{ height: rowHeight }}>
                      <div className="sticky left-0 z-10 w-40 bg-white group-hover:bg-slate-50 border-r border-gray-200 p-3 flex flex-col justify-center shadow-[2px_0_5px_rgba(0,0,0,0.05)]" style={{ height: rowHeight }}>
                         <div className="font-bold text-slate-800 text-sm truncate" title={group.name}>{group.name}</div>
                         <div className="text-[10px] text-slate-400 mt-1">{group.items.length} 个排班</div>
                      </div>
                      <div className="flex py-2 relative" style={{ height: rowHeight }}> 
                         <div className="absolute inset-0 flex pointer-events-none">
                            {dateRange.map((d, i) => (
                              <div key={i} style={{ width: 40, minWidth: 40 }} className={`flex-shrink-0 border-r border-gray-50 h-full ${d.getTime() === today.getTime() ? 'bg-red-50/30' : ''}`}></div>
                            ))}
                         </div>
                         {group.lanes.map((lane, laneIdx) => (
                            <React.Fragment key={laneIdx}>
                                {lane.map((item) => {
                                  const startDiff = getDaysDiff(dateRange[0], item.startDate);
                                  const duration = getDaysDiff(item.startDate, item.endDate) + 1;
                                  const isTentative = item.isPredicted;
                                  const isFuture = item.startDate > today;
                                  const offsetLeft = startDiff * 40; 
                                  const width = duration * 40;
                                  const offsetTop = 10 + (laneIdx * 42); 
                                  const typeColor = TYPE_COLORS[item.type]?.bg || 'bg-gray-500';
                                  const days = Array.from({length: duration}, (_, k) => getDayConfig(item.type, k, isTentative ?? false, isFuture));
                                  const patternStyle = isTentative ? { backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 4px, transparent 4px, transparent 8px)' } : {};
                                  return (
                                    <div 
                                      key={item.id}
                                      onClick={() => setSelectedItem(item)}
                                      className="absolute rounded-lg border overflow-visible shadow-sm hover:shadow-md transition-all cursor-pointer hover:brightness-95 hover:z-20 flex text-[10px]"
                                      style={{ left: `${offsetLeft}px`, width: `${width}px`, top: `${offsetTop}px`, height: '36px', borderColor: isTentative ? '#cbd5e1' : (isFuture ? '#e9d5ff' : '#e2e8f0'), borderStyle: isTentative ? 'dashed' : 'solid', ...patternStyle }}
                                    >
                                      <div className={`absolute -top-3 left-0 px-2 py-0.5 rounded text-[9px] font-bold text-white whitespace-nowrap z-30 shadow-sm ${isTentative ? 'bg-indigo-400' : typeColor}`} style={{ maxWidth: '200px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }} title={item.groupCode}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                                          <span className="truncate" style={{ maxWidth: '120px' }}>{item.groupCode}</span>
                                          <span className={`text-[9px] px-1 py-0.5 rounded ${TYPE_COLORS[item.type]?.bg || 'bg-gray-500'}`}>
                                            {TYPE_COLORS[item.type]?.label || item.type}
                                          </span>
                                        </div>
                                        {/* 如果上传原始行包含更准确的群号，则在左上以较小字号显示（不覆盖主群号） */}
                                        {(() => {
                                          const rawG = getRawGroupFromItem(item);
                                          if (rawG && rawG !== item.groupCode) {
                                            return <div className="text-[8px] text-white/80 font-medium">{rawG}</div>;
                                          }
                                          return null;
                                        })()}
                                      </div>
                                      {days.map((dayCfg, dIdx) => (
                                         <div key={dIdx} className={`flex-1 flex flex-col items-center justify-center leading-none overflow-hidden ${dIdx === 0 ? 'rounded-l-lg' : ''} ${dIdx === days.length -1 ? 'rounded-r-lg' : ''} ${dayCfg.style}`} title={dayCfg.title}><span className="scale-90">{dayCfg.label}</span></div>
                                      ))}
                                    </div>
                                  );
                                })}
                            </React.Fragment>
                         ))}
                      </div>
                   </div>
                 );
               })}
            </div>
         </div>
      </div>
      {selectedItem && (
        <ScheduleModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </div>
  );
};

export default GanttView;
